<script lang="ts" setup>
import { computed, ref } from 'vue';
import api from '@/api';
import { DgrDialog } from '@stockmarkteam/donguri-ui';
import Loading from '@/components/common/loading.vue';
import { useSnackbar } from '@/components/common/snackbar/use-snackbar';
import CreateFollowListForm, {
  type FollowListSelection,
} from '@/components/home/follow-lists/create-follow-list-form.vue';
import { FollowListType } from '@/types';

// このコンポーネントはDgrDialogをラップしているだけなので、openの値をこのコンポーネント内で変更しない。
const open = defineModel<boolean>('open', { default: false });

const { createSnackbar } = useSnackbar();

const emits = defineEmits<{
  submit: [followList: FollowListType];
  close: [];
}>();

const isSubmitting = ref(false);
const selection = ref<FollowListSelection | null>(null);
const isLoading = ref(true);

const isDisabled = computed(() => {
  if (isSubmitting.value || isLoading.value) return true;
  return selection.value === null;
});

const onSubmit = async () => {
  if (!selection.value) return;

  isSubmitting.value = true;
  try {
    const createdFollowList = await api.createFollowList(
      [selection.value],
      selection.value.type === 'organization_tag' ? 'recommended' : 'latest',
    );
    emits('submit', createdFollowList);
    createSnackbar({
      message: '新規リストを追加しました。',
      type: 'success',
    });
    open.value = false;
  } catch (error) {
    createSnackbar({
      message: '新規リストの作成に失敗しました',
      type: 'error',
    });
    if (error instanceof Error) {
      throw error;
    }
    throw new Error('予期しないエラーが発生しました');
  } finally {
    isSubmitting.value = false;
  }
};

const onCancel = () => {
  open.value = false;
};
</script>

<template>
  <template v-if="isSubmitting">
    <Loading z-index="9999" />
  </template>
  <div class="wrapper">
    <DgrDialog v-model:open="open" title="新規リストの作成" with-footer-divider>
      <template #description>
        <CreateFollowListForm
          :disabled="isSubmitting"
          @select="selection = $event"
          @loading="isLoading = $event"
          class="create-follow-list-form"
        />
      </template>

      <template #footer>
        <button
          :class="{ disabled: isSubmitting }"
          :disabled="isSubmitting"
          class="c-btn c-btn--auto c-btnOutline"
          @click="onCancel"
        >
          キャンセル
        </button>
        <button
          class="c-btn c-btn--auto c-btn--AnewsPrimary"
          :class="{ disabled: isDisabled }"
          :disabled="isDisabled"
          @click="onSubmit"
        >
          追加
        </button>
      </template>
    </DgrDialog>
  </div>
</template>

<style lang="scss" scoped>
.wrapper {
  border: 1px solid $color-gray400;
  border-radius: 8px;
  background-color: $color-white;
}

.create-follow-list-form {
  width: 768px;
  height: 370px;
}
</style>
